import type { WorkflowGraphEdge, WorkflowGraphNode } from '../../api/workflowRuns'
import { resolveWorkflowNodeLabel } from './workflowDefinitionDisplay'
import { workflowTypeFromGraph, type WorkflowType } from './workflowTemplates'
import {
  WORKFLOW_GRAPH_LINT_CODES,
  workflowControlOperatorLabel,
  workflowControlOperatorNeedsExpectValue,
} from './workflowControlFlow'
import {
  resolveWorkflowControlInputSource,
  resolveWorkflowMainInputSource,
  workflowControlInputSourceIssueMessage,
  workflowMainInputBindingFieldKey,
  workflowMainInputSourceIssueMessage,
  type WorkflowControlGraphSlice,
} from './workflowControlSourceOptions'

export type WorkflowGraphLintLevel = 'error' | 'warning'

export type WorkflowGraphLintIssue = {
  level: WorkflowGraphLintLevel
  code: string
  message: string
  nodeId?: string
}

function lintNodeName(node: WorkflowGraphNode, workflowType?: WorkflowType): string {
  return resolveWorkflowNodeLabel(node, workflowType ?? 'custom')
}

function normalizeSourcePort(edge: WorkflowGraphEdge): string {
  const port = String(edge.sourcePort ?? '').trim()
  if (port) return port
  const route = String(edge.route ?? '').trim()
  if (route === 'pass' || route === 'fail' || route === 'body' || route === 'done') return route
  return 'out'
}

function controlOperatorNeedsExpectValue(operator: string): boolean {
  return workflowControlOperatorNeedsExpectValue(operator)
}

function collectLoopBodySubgraph(loopNodeId: string, edges: WorkflowGraphEdge[]): Set<string> {
  const nodes = new Set<string>()
  const queue: string[] = []
  for (const edge of edges) {
    if (edge.sourceNodeId !== loopNodeId) continue
    if (normalizeSourcePort(edge) !== 'body') continue
    const target = String(edge.targetNodeId ?? '').trim()
    if (target && target !== loopNodeId) queue.push(target)
  }
  while (queue.length > 0) {
    const current = queue.shift()!
    if (!nodes.add(current)) continue
    for (const edge of edges) {
      if (edge.sourceNodeId !== current) continue
      const target = String(edge.targetNodeId ?? '').trim()
      if (!target || target === loopNodeId) continue
      queue.push(target)
    }
  }
  return nodes
}

function loopBodyReturnsToLoop(loopNodeId: string, edges: WorkflowGraphEdge[]): boolean {
  const bodyNodes = collectLoopBodySubgraph(loopNodeId, edges)
  if (bodyNodes.size === 0) return false
  return edges.some((edge) => edge.targetNodeId === loopNodeId && bodyNodes.has(edge.sourceNodeId))
}

function lintControlExpression(
  node: WorkflowGraphNode,
  graph: WorkflowControlGraphSlice,
  workflowType?: WorkflowType,
): WorkflowGraphLintIssue[] {
  const nodeName = lintNodeName(node, workflowType)
  const issues: WorkflowGraphLintIssue[] = []
  const config = (node.config ?? {}) as Record<string, unknown>
  const operator = String(config.operator ?? 'not_empty').trim() || 'not_empty'
  const expectValue = String(config.expectValue ?? '').trim()
  const wiringIssue = workflowControlInputSourceIssueMessage(graph, node)
  const resolved = resolveWorkflowControlInputSource(graph, node)
  if (wiringIssue || !resolved) {
    issues.push({
      level: 'error',
      code: 'CONTROL_SOURCE_FIELD_MISSING',
      message: wiringIssue ?? `「${nodeName}」无法从主输入推导判断依据`,
      nodeId: node.nodeId,
    })
  }
  if (controlOperatorNeedsExpectValue(operator) && !expectValue) {
    issues.push({
      level: 'warning',
      code: 'CONTROL_EXPECT_VALUE_MISSING',
      message: `「${nodeName}」分支条件为「${workflowControlOperatorLabel(operator)}」时需填写比较值`,
      nodeId: node.nodeId,
    })
  }
  return issues
}

function reachableFrom(startId: string, edges: WorkflowGraphEdge[]): Set<string> {
  const visited = new Set<string>([startId])
  const queue = [startId]
  while (queue.length > 0) {
    const current = queue.shift()!
    for (const edge of edges) {
      if (edge.sourceNodeId !== current) continue
      const target = String(edge.targetNodeId ?? '').trim()
      if (!target || visited.has(target)) continue
      visited.add(target)
      queue.push(target)
    }
  }
  return visited
}

function lintMainInputBinding(
  node: WorkflowGraphNode,
  graph: WorkflowControlGraphSlice,
  workflowType?: WorkflowType,
): WorkflowGraphLintIssue[] {
  const nodeName = lintNodeName(node, workflowType)
  const bindingKey = workflowMainInputBindingFieldKey(node.type)
  if (!bindingKey) return []
  const issues: WorkflowGraphLintIssue[] = []
  const wiringIssue = workflowMainInputSourceIssueMessage(graph, node)
  const resolved = resolveWorkflowMainInputSource(graph, node)
  if (wiringIssue || !resolved) {
    issues.push({
      level: 'error',
      code: 'MAIN_INPUT_BINDING_MISSING',
      message: wiringIssue ?? `「${nodeName}」无法从主输入推导数据来源`,
      nodeId: node.nodeId,
    })
    return issues
  }
  const config = (node.config ?? {}) as Record<string, unknown>
  const bound = String(config[bindingKey] ?? '').trim()
  if (bound && bound !== resolved.fieldKey) {
    issues.push({
      level: 'warning',
      code: 'MAIN_INPUT_BINDING_STALE',
      message: `「${nodeName}」数据来源与主输入连线不一致，保存前将自动校正`,
      nodeId: node.nodeId,
    })
  }
  return issues
}

/** 工作流图静态检查（与后端 WorkflowGraphValidator 规则对齐）。 */
export function lintWorkflowGraph(
  nodes: WorkflowGraphNode[],
  edges: WorkflowGraphEdge[],
  inputSchema?: Record<string, unknown>,
): WorkflowGraphLintIssue[] {
  const workflowType = workflowTypeFromGraph({ inputSchema: inputSchema ?? {} })
  const issues: WorkflowGraphLintIssue[] = []
  if (nodes.length === 0) {
    issues.push({ level: 'error', code: 'GRAPH_EMPTY', message: '至少需要一个节点' })
    return issues
  }

  const start = nodes.find((node) => node.type === 'start.input')
  if (!start) {
    issues.push({ level: 'error', code: 'START_MISSING', message: '必须包含一个开始节点' })
    return issues
  }

  const reachable = reachableFrom(start.nodeId, edges)
  for (const node of nodes) {
    if (node.type !== 'end.output') continue
    if (!reachable.has(node.nodeId)) {
      const nodeName = lintNodeName(node, workflowType)
      issues.push({
        level: 'error',
        code: 'END_UNREACHABLE',
        message: `结束节点「${nodeName}」从开始节点不可达，请检查连线`,
        nodeId: node.nodeId,
      })
    }
  }

  for (const node of nodes) {
    const nodeName = lintNodeName(node, workflowType)
    const hasIn = edges.some((edge) => edge.targetNodeId === node.nodeId)
    const hasOut = edges.some((edge) => edge.sourceNodeId === node.nodeId)
    if (!hasIn && !hasOut) {
      issues.push({
        level: 'warning',
        code: 'NODE_ISOLATED',
        message: `节点「${nodeName}」未连接任何连线`,
        nodeId: node.nodeId,
      })
    } else if (node.type !== 'start.input' && !hasIn) {
      issues.push({
        level: 'warning',
        code: 'NODE_NO_INCOMING',
        message: `节点「${nodeName}」缺少上游连线`,
        nodeId: node.nodeId,
      })
    }
  }

  for (const node of nodes) {
    const nodeName = lintNodeName(node, workflowType)
    const graph = { nodes, edges }
    if (node.type === 'condition.check') {
      issues.push(...lintControlExpression(node, graph, workflowType))
      const outPorts = new Set(
        edges.filter((edge) => edge.sourceNodeId === node.nodeId).map((edge) => normalizeSourcePort(edge)),
      )
      if (outPorts.has('pass') && !outPorts.has('fail')) {
        issues.push({
          level: 'warning',
          code: 'CONDITION_FAIL_UNCONNECTED',
          message: `条件判断「${nodeName}」仅连接了「满足」分支，建议补上「不满足」分支`,
          nodeId: node.nodeId,
        })
      }
      if (outPorts.has('fail') && !outPorts.has('pass')) {
        issues.push({
          level: 'warning',
          code: 'CONDITION_PASS_UNCONNECTED',
          message: `条件判断「${nodeName}」仅连接了「不满足」分支，建议补上「满足」分支`,
          nodeId: node.nodeId,
        })
      }
      continue
    }
    if (node.type === 'loop.until') {
      issues.push(...lintControlExpression(node, graph, workflowType))
      const outPorts = new Set(
        edges.filter((edge) => edge.sourceNodeId === node.nodeId).map((edge) => normalizeSourcePort(edge)),
      )
      if (outPorts.has('body') && !outPorts.has('done')) {
        issues.push({
          level: 'warning',
          code: 'LOOP_DONE_UNCONNECTED',
          message: `循环节点「${nodeName}」仅连接了「循环体」分支，建议补上「退出」分支`,
          nodeId: node.nodeId,
        })
      }
      if (outPorts.has('done') && !outPorts.has('body')) {
        issues.push({
          level: 'warning',
          code: WORKFLOW_GRAPH_LINT_CODES.LOOP_BODY_UNCONNECTED,
          message: `循环节点「${nodeName}」仅连接了「退出」分支，建议补上「循环体」分支`,
          nodeId: node.nodeId,
        })
      }
      const bodyNodes = collectLoopBodySubgraph(node.nodeId, edges)
      if (outPorts.has('body') && bodyNodes.size === 0) {
        issues.push({
          level: 'warning',
          code: WORKFLOW_GRAPH_LINT_CODES.LOOP_BODY_EMPTY,
          message: `循环节点「${nodeName}」的「循环体」分支未连接任何节点`,
          nodeId: node.nodeId,
        })
      }
      if (bodyNodes.size > 0 && !loopBodyReturnsToLoop(node.nodeId, edges)) {
        issues.push({
          level: 'warning',
          code: WORKFLOW_GRAPH_LINT_CODES.LOOP_BODY_NOT_CLOSED,
          message: `循环节点「${nodeName}」的循环体未回连至本节点，将无法进入下一轮`,
          nodeId: node.nodeId,
        })
      }
    }
    if (workflowMainInputBindingFieldKey(node.type)) {
      issues.push(...lintMainInputBinding(node, { nodes, edges }, workflowType))
    }
  }

  return issues
}

export function workflowGraphLintHasErrors(issues: WorkflowGraphLintIssue[]): boolean {
  return issues.some((issue) => issue.level === 'error')
}

export function workflowGraphLintErrors(issues: WorkflowGraphLintIssue[]): WorkflowGraphLintIssue[] {
  return issues.filter((issue) => issue.level === 'error')
}

export function workflowGraphLintWarnings(issues: WorkflowGraphLintIssue[]): WorkflowGraphLintIssue[] {
  return issues.filter((issue) => issue.level === 'warning')
}
